import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { company } from '~/company/Company';
import { EmptyWithFilters } from '~/components/EmptyWithFilters';
import ProductCard from '~/components/ProductCard/ProductCard';
import ProductCardSkeletonM from '~/components/ProductCardV3/skeletons/ProductCardSkeletonM';
import { ProductListAnalyticsProvider } from '~/components/ProductListAnalytics';
import EmptyState from '~/pages/Catalog/EmptyState';
import { InfinityScrollLoader } from '~/pages/Catalog/InfinityScrollLoader';
import { categoryProductList } from '~/services/analytics/productLists';
import { orderStore } from '~/stores';
import type { CatalogCategory } from '~/stores/CategoriesStore';

import styles from './ProductsView.module.scss';

const ProductsView = ({
  className,
  category,
  handleResetFilters,
}: {
  className?: string;
  category: CatalogCategory;
  handleResetFilters: (options?: { mobile?: boolean }) => void;
}) => {
  const rootRef = useRef<Nullable<HTMLDivElement>>(null);
  const [searchParams] = useSearchParams();
  const showProducts = searchParams.get('showProducts') === 'true';

  if (!category.data) {
    return (
      <div className={styles.root}>
        <div className={styles.grid}>{getSkeletons(PAGE_SKELETONS)}</div>
      </div>
    );
  }

  if (category.isNotFound) {
    return (
      <div className={styles.notFound}>
        <EmptyState />
      </div>
    );
  }

  if (!category.products?.length) {
    if (showProducts && category.paginationRequest.isLoading) {
      // Reloading a category whose size is already known — a filter change, a
      // sort, a warehouse switch. Drawing a full page of placeholders for a
      // category that returns nine products collapses the grid the moment they
      // arrive, and that collapse is a layout shift we can simply not cause.
      const known = category.paginationRequest.total;

      return (
        <div className={styles.root}>
          <div className={styles.grid}>
            {getSkeletons(
              known ? Math.min(PAGE_SKELETONS, known) : PAGE_SKELETONS,
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.emptyCategory}>
        <EmptyWithFilters onReset={() => handleResetFilters()} />
      </div>
    );
  }

  return (
    <ProductListAnalyticsProvider
      list={categoryProductList(category.data.id, category.data.name)}
    >
      <div className={classes([styles.root, className])} ref={rootRef}>
        {category.data && category.paginationRequest.isLoading && (
          <div className={styles.loader}>
            <span className="spinner" />
          </div>
        )}
        <div className={styles.grid}>
          {category.products.map((product, index) => (
            <ProductCard
              key={`${product.slug}_${product.id}_${product.productId}`}
              product={product}
              index={index}
              priority={index === 0}
            />
          ))}
          {category.paginationRequest.canLoadMore && (
            <InfinityScrollLoader
              embedded
              className={styles.grid}
              onLoadMore={() => {
                const reloading = category.paginationRequest.isLoading;
                const pending = category.paginationRequest.loadMore(
                  orderStore.etaWarehouseCode,
                );
                if (!reloading) {
                  category.paginationRequest.setStatus('fulfilled');
                }
                return pending;
              }}
              skeletons={<>{getSkeletons(4)}</>}
            />
          )}
        </div>
      </div>
    </ProductListAnalyticsProvider>
  );
};

/**
 * Placeholders for one page of results — matches the `size` the catalog
 * requests, so the grid does not resize when the real products replace them.
 */
const PAGE_SKELETONS = 48;

const getSkeletons = (length: number) =>
  Array.from({ length }, () => uuid()).map((key) => (
    <ProductCardSkeletonM key={key} />
  ));

export default observer(ProductsView);
